<template>
  <div class="section-nav" role="tablist" aria-orientation="vertical" aria-label="Secciones de su expediente">
    <button
      v-for="card in cards"
      :id="'tab-' + card.tab"
      :key="card.step"
      type="button"
      role="tab"
      class="section-card"
      :class="[card.state, { active: active === card.tab, 'has-alerts': card.hasAlerts }]"
      :aria-selected="active === card.tab ? 'true' : 'false'"
      :aria-controls="'panel-' + card.tab"
      :tabindex="active === card.tab ? 0 : -1"
      @click="$emit('select', card.tab)"
      @keydown="onKeydown"
    >
      <span class="section-card-icon">
        <PortalIcon v-if="card.icon" :name="card.icon" :size="16" />
        <span v-else class="section-card-number">{{ card.number }}</span>
      </span>
      <span class="section-card-title">{{ card.title }}</span>
      <span class="section-card-status">{{ card.status }}</span>
    </button>
  </div>
</template>

<script>
import PortalIcon from "./PortalIcon.vue";
import { TAB_SECTIONS, sectionState } from "./candidateSummary";

// What each chip shows by state; a section still to come (locked) shows its number instead of an icon.
const SECTION_ICONS = { done: "check", current: "edit", locked: null };

// One row per step that has a tab, with its state, in a card of the side column; they are the portal's navigation (the
// row of the open section is dark). The declaration, the last step, is not one of them: it opens in a modal.
export default {
  name: "PortalSectionNav",
  components: { PortalIcon },
  props: {
    candidate: { type: Object, required: true },
    // Number of documents to update (documentAlerts() of the candidate), flagged on the Documentación row.
    alertsCount: { type: Number, default: 0 },
    // `tab` key of the open section (see TAB_SECTIONS).
    active: { type: String, required: true },
  },
  computed: {
    cards() {
      return TAB_SECTIONS.map((section) => {
        const state = sectionState(section.step, this.candidate);
        const hasAlerts = state === "done" && section.step === 1 && this.alertsCount > 0;
        let status = { done: "Completo", current: "Siguiente paso", locked: "Pendiente" }[state];
        if (hasAlerts) status = `${this.alertsCount} por actualizar`;
        return {
          ...section,
          state,
          status,
          hasAlerts,
          icon: hasAlerts ? "exclamation" : SECTION_ICONS[state],
          number: String(section.step + 1).padStart(2, "0"),
        };
      });
    },
  },
  methods: {
    onKeydown(e) {
      const moves = { ArrowDown: 1, ArrowRight: 1, ArrowUp: -1, ArrowLeft: -1 };
      if (!moves[e.key]) return;
      e.preventDefault();
      const idx = this.cards.findIndex((card) => card.tab === this.active);
      const next = this.cards[(idx + moves[e.key] + this.cards.length) % this.cards.length];
      this.$emit("select", next.tab);
      this.$nextTick(() => {
        const button = document.getElementById("tab-" + next.tab);
        if (button) button.focus();
      });
    },
  },
};
</script>

<style scoped>
/* A card of the side column: the steps one under another, each with a chip for its state. */
.section-nav {
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 10px;
  border-radius: 20px;
  background: #fff;
  box-shadow: 0 1px 2px rgba(10, 16, 32, 0.05), 0 8px 24px -10px rgba(10, 16, 32, 0.14);
}
.section-card {
  display: grid;
  grid-template-columns: 36px minmax(0, 1fr);
  grid-template-areas:
    "icon title"
    "icon status";
  align-items: center;
  column-gap: 14px;
  row-gap: 1px;
  width: 100%;
  min-width: 0;
  padding: 14px;
  border: 0;
  border-radius: 14px;
  background: transparent;
  text-align: left;
  cursor: pointer;
  transition: background-color 0.2s;
}
.section-card:hover {
  background: #f6f7fa;
}
.section-card:focus {
  outline: none;
}
.section-card:focus-visible {
  outline: 2px solid #e6b65c;
  outline-offset: 2px;
}
.section-card-icon {
  grid-area: icon;
  box-sizing: border-box;
  width: 36px;
  height: 36px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
}
.section-card-icon svg {
  width: 16px;
  height: 16px;
  stroke-width: 2.2;
}
.done .section-card-icon {
  background: #eaf8f1;
  color: #0f7a4f;
  box-shadow: inset 0 0 0 1px #bfe8d3;
}
.done .section-card-icon svg {
  stroke-width: 2.8;
}
.done.has-alerts .section-card-icon {
  background: #fff1e2;
  color: #b4540a;
  box-shadow: inset 0 0 0 1px #fad9b0;
}
.done.has-alerts .section-card-icon svg {
  stroke-width: 2.8;
}
.current .section-card-icon {
  background: #fff3dc;
  color: #9a6a1c;
  box-shadow: inset 0 0 0 1px #f1d9a8;
}
/* A step still to come: its number in a dotted chip. */
.locked .section-card-icon {
  border: 1.5px dotted #cfd4de;
  color: #8a91a3;
}
.section-card-number {
  font: 500 12px "Geist Mono", ui-monospace, monospace;
}
.section-card-title {
  grid-area: title;
  align-self: end;
  font: 600 15px "Geist", system-ui, sans-serif;
  color: #0e1424;
}
.section-card-status {
  grid-area: status;
  align-self: start;
  font: 400 13px "Geist", system-ui, sans-serif;
  color: #6b7283;
}
.current .section-card-status {
  color: #9a6a1c;
  font-weight: 600;
}
.has-alerts .section-card-status {
  color: #b4540a;
  font-weight: 500;
}

/* The open section: dark. Its chip keeps the light look of its state. */
.section-card.active,
.section-card.active:hover {
  background: #0e1424;
  box-shadow: 0 10px 22px -12px rgba(10, 16, 32, 0.7);
}
.active .section-card-title {
  color: #fff;
}
.active .section-card-status {
  color: #a9b2c6;
}
.active.current .section-card-status,
.active.has-alerts .section-card-status {
  color: #e6b65c;
}
.active.locked .section-card-icon {
  border-color: rgba(255, 255, 255, 0.35);
  color: #c0c8d8;
}
</style>
